<template>
  <div class="menu-container">
    <!-- Header -->
    <v-card rounded="xl" elevation="0" class="header-card">
      <div class="header-content">
        <div class="logo-wrapper">
          <img :src="logo" class="toolbar-logo" alt="Logo" />
        </div>
        <div class="header-title">{{ $t('menu.menu') }}</div>
        <v-btn v-if="profileLoaded" icon variant="text" size="small" class="refresh-btn">
          <v-icon color="white" size="22">mdi-refresh</v-icon>
        </v-btn>
      </div>
    </v-card>

    <!-- Profile Card -->
    <v-card rounded="xl" elevation="0" class="profile-card mt-3">
      <div class="profile-content">
        <div class="avatar-section">
          <div class="avatar-glow"></div>
          <v-avatar v-if="profileLoaded" :size="isMobile ? 80 : 100" class="profile-avatar">
            <v-img :src="userProfile.profileImage" cover />
          </v-avatar>
          <v-progress-circular v-else :size="isMobile ? 40 : 50" width="3" color="#23A5E6" indeterminate />
          <div class="online-indicator">
            <v-icon size="10" color="white">mdi-check</v-icon>
          </div>
        </div>

        <div v-if="profileLoaded" class="profile-details">
          <h2 class="user-name">{{ userProfile.name || userProfile.email }}</h2>
          
          <div class="job-badge">
            <v-icon size="14" color="#285096">mdi-briefcase-outline</v-icon>
            <span>{{ userProfile.jobTitle }}</span>
          </div>

          <div class="email-row">
            <v-icon size="14" color="#64748B">mdi-email-outline</v-icon>
            <span class="text-truncate">{{ userProfile.email }}</span>
          </div>

          <div v-if="userRoles.length > 0" class="roles-row">
            <v-chip
              v-for="role in userRoles.slice(0, 2)"
              :key="role"
              :color="getRoleColor(role)"
              variant="flat"
              size="x-small"
              class="role-chip"
            >
              {{ formatRoleName(role) }}
            </v-chip>
          </div>
        </div>

        <v-btn
          v-if="profileLoaded && !isMobile"
          variant="flat"
          color="#285096"
          rounded="lg"
          class="edit-profile-btn text-none"
          prepend-icon="mdi-pencil-outline"
          @click="router.push({ name: 'UserProfile' })"
        >
          Edit Profile
        </v-btn>
      </div>
    </v-card>

    <!-- Menu Grid -->
    <div class="menu-grid mt-3">
      <div 
        v-for="item in menuItems" 
        :key="item.title" 
        class="menu-item"
        @click="goToMenuItem(item)"
      >
        <div class="menu-card" :class="{ 'card-disabled': item.disabled }">
          <!-- Premium Badge -->
          <div v-if="item.isPremium" class="premium-tag">
            <v-icon size="10">mdi-star</v-icon>
            <span>PRO</span>
          </div>

          <!-- Locked Overlay -->
          <div v-if="item.disabled" class="locked-overlay">
            <v-icon size="28" color="white">mdi-lock-outline</v-icon>
            <span class="locked-text">Locked</span>
          </div>

          <!-- Card Content -->
          <div class="card-inner">
            <div class="icon-box" :style="{ background: item.background }">
              <v-icon :style="{ color: item.color }" class="card-icon">{{ item.icon }}</v-icon>
            </div>
            
            <div class="card-text">
              <div class="card-title">{{ item.title }}</div>
              <div class="card-desc">{{ item.subtitle }}</div>
            </div>
          </div>

          <!-- Hover Arrow -->
          <div class="card-arrow">
            <v-icon size="16" color="white">mdi-chevron-right</v-icon>
          </div>
        </div>
      </div>
    </div>

    <!-- Admin Panel Dialog -->
    <v-dialog v-model="dialog" max-width="500" transition="dialog-bottom-transition">
      <v-card rounded="xl" class="dialog-card">
        <v-card-title class="dialog-header">
          <v-icon color="white" class="mr-2">mdi-shield-account-outline</v-icon>
          <span>{{ dialogType === 'roomControl' ? 'Room Control' : 'Admin Panel' }}</span>
          <v-spacer />
          <v-btn icon variant="text" size="small" @click="dialog = false">
            <v-icon color="white">mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="dialog-body">
          <div class="dialog-grid">
            <div
              v-for="control in dialogType === 'roomControl' ? roomControls : adminControls"
              :key="control.title"
              class="dialog-item"
              :class="{ 'item-disabled': control.disabled }"
              @click="dialogType === 'roomControl' ? goToHardwareControl(control) : goToAdminControl(control)"
            >
              <div v-if="control.isPremium" class="dialog-premium-tag">PRO</div>
              <div v-if="control.disabled" class="dialog-locked">
                <v-icon size="20" color="white">mdi-lock</v-icon>
              </div>
              <div class="dialog-icon" :style="{ background: control.tint || '#F1F5F9', color: control.color }">
                <v-icon :color="control.color" size="28">{{ control.icon }}</v-icon>
              </div>
              <div class="dialog-title">{{ control.title }}</div>
            </div>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Language Dialog -->
    <v-dialog v-model="showLanguageDialog" max-width="360">
      <v-card rounded="xl" class="language-dialog">
        <v-card-title class="lang-header">
          <v-icon color="#285096" class="mr-2">mdi-translate</v-icon>
          {{ $t('menu.language-selection') }}
        </v-card-title>

        <v-card-text class="lang-body">
          <div class="lang-options">
            <div 
              class="lang-option" 
              :class="{ 'lang-selected': selectedLanguage === 'en' }"
              @click="setLanguage('en')"
            >
              <v-img :src="usa_flag" width="40" height="40" class="lang-flag" />
              <span class="lang-name">{{ $t('menu.english') }}</span>
              <v-icon v-if="selectedLanguage === 'en'" color="#28A745" size="20">mdi-check-circle</v-icon>
            </div>
            <div 
              class="lang-option" 
              :class="{ 'lang-selected': selectedLanguage === 'de' }"
              @click="setLanguage('de')"
            >
              <v-img :src="german_flag" width="40" height="40" class="lang-flag" />
              <span class="lang-name">{{ $t('menu.german') }}</span>
              <v-icon v-if="selectedLanguage === 'de'" color="#28A745" size="20">mdi-check-circle</v-icon>
            </div>
          </div>
        </v-card-text>

        <v-card-actions class="lang-actions">
          <v-btn variant="text" color="#64748B" rounded="lg" class="text-none" @click="showLanguageDialog = false">
            {{ $t('menu.cancel') }}
          </v-btn>
          <v-spacer />
          <v-btn variant="flat" color="#285096" rounded="lg" class="text-none px-6" @click="confirmLanguageChange">
            {{ $t('menu.change') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
  import { ref, defineComponent, onMounted, watch, computed } from 'vue'
  import { getToolbarLogoImage } from '@/assets/Branding/branding.js'
  import { useRouter } from 'vue-router'
  import { Device } from '@capacitor/device'
  import { Capacitor } from '@capacitor/core'
  import { isIos } from '@/services/OAuthService'
  import { isWebPlatform, setCookie, getCookie } from '@/services/utils'
  import { useOAuthService } from '@/services/OAuthService'
  import { performLogout } from '@/controllers/BackboneAPI'
  import Swal from 'sweetalert2'
  import user_avatar_3d from '@/assets/General/user_avatar_3d.png'
  import { getUserProfiles, getUserProfileById, getFile, getDataFromBlob, getFeatures, isPIA } from '@/controllers/BaseController'
  import { storeBlob } from '@/services/utils'
  import lodash from 'lodash'
  import { useI18n } from 'vue-i18n'
  import german_flag from '@/assets/General/german_flag.svg'
  import usa_flag from '@/assets/General/usa_flag.svg'
  import { Browser } from '@capacitor/browser'
  import { useDisplay } from 'vuetify'

  export default defineComponent({
    name: 'MenuView',
    setup() {
      const { locale, t } = useI18n()
      const { mobile, width } = useDisplay()
      const isMobile = computed(() => mobile.value)

      const showLanguageDialog = ref(false)
      const selectedLanguage = ref('en')
      const logo = ref('')
      const dialog = ref(false)
      const dialogType = ref('')
      const isPia = ref(false)
      const isAdmin = ref(false)
      const isPremiumUser = ref(false)
      const profileLoaded = ref(false)
      const roomControls = ref([{ title: 'Room Control', icon: 'mdi-lightbulb-outline' }])

      const adminControls = computed(() => [
        {
          title: 'Feedbacks',
          icon: 'mdi-message-outline',
          color: '#28A745',
          tint: '#ECFDF5',
          disabled: !isPremiumUser.value,
          isPremium: true
        },
        {
          title: 'Bookings',
          icon: 'mdi-calendar-check-outline',
          color: '#23A5E6',
          tint: '#EFF6FF',
          disabled: !isPremiumUser.value,
          isPremium: true
        },
        {
          title: 'Users',
          icon: 'mdi-account-supervisor',
          color: '#8E44AD',
          tint: '#FAF5FF',
          disabled: !isPremiumUser.value,
          isPremium: true
        },
        {
          title: 'Incidents',
          icon: 'mdi-alert-circle-outline',
          color: '#DC3545',
          tint: '#FEF2F2',
          disabled: false,
          isPremium: false
        },
        {
          title: 'Remote Work',
          icon: 'mdi-briefcase-clock-outline',
          color: '#F39C12',
          tint: '#FFFBEB',
          disabled: false,
          isPremium: false
        },
      ])

      const router = useRouter()
      const OAuthService = useOAuthService()
      const userProfile = ref(null)

      const menuItems = ref([])

      const userRoles = computed(() => {
        if (!userProfile.value?.realm_access?.roles) return []
        const excludedRoles = ['offline_access', 'uma_authorization', 'default-roles-pinestack']
        return userProfile.value.realm_access.roles
          .filter(role => !excludedRoles.includes(role))
          .filter(role => role)
      })

      const formatRoleName = (role) => {
        if (!role) return ''
        return role
          .replace(/([A-Z])/g, ' $1')
          .replace(/_/g, ' ')
          .replace(/^\w/, c => c.toUpperCase())
          .trim()
      }

      const getRoleColor = (role) => {
        if (!role) return '#4E5965'
        const lowerRole = role.toLowerCase()
        if (lowerRole.includes('admin') || lowerRole.includes('global')) return '#DC3545'
        if (lowerRole.includes('manager') || lowerRole.includes('supervisor')) return '#F39C12'
        if (lowerRole.includes('user') || lowerRole.includes('member')) return '#23A5E6'
        if (lowerRole.includes('viewer') || lowerRole.includes('guest')) return '#95A5A6'
        return '#28A745'
      }

      const updateMenuItems = () => {
        menuItems.value = [
          {
            title: t('menu.profile.title'),
            subtitle: t('menu.profile.subtitle'),
            icon: 'mdi-account-outline',
            color: 'white',
            background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
            disabled: false,
            isPremium: false
          },
          {
            title: t('menu.room_control.title'),
            subtitle: t('menu.room_control.subtitle'),
            icon: 'mdi-lightbulb-outline',
            color: 'white',
            background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
            disabled: !isPremiumUser.value,
            isPremium: true
          },
          {
            title: t('menu.locks_control.title'),
            subtitle: t('menu.locks_control.subtitle'),
            icon: 'mdi-lock-outline',
            color: 'white',
            background: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
            disabled: false,
            isPremium: false
          },
          {
            title: t('menu.elevator_control.title'),
            subtitle: t('menu.elevator_control.subtitle'),
            icon: 'mdi-elevator-passenger-outline',
            color: 'white',
            background: 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)',
            disabled: !isPremiumUser.value,
            isPremium: true
          },
          {
            title: t('menu.analytics.title'),
            subtitle: t('menu.analytics.subtitle'),
            icon: 'mdi-chart-line',
            color: 'white',
            background: 'linear-gradient(135deg, #F97316 0%, #EA580C 100%)',
            disabled: false,
            isPremium: false
          },
          {
            title: t('menu.occupancy.title'),
            subtitle: t('menu.occupancy.subtitle'),
            icon: 'mdi-map-marker-radius-outline',
            color: 'white',
            background: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)',
            disabled: false,
            isPremium: false
          },
          {
            title: t('menu.incidents.title'),
            subtitle: t('menu.incidents.subtitle'),
            icon: 'mdi-alert-circle-outline',
            color: 'white',
            background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
            disabled: false,
            isPremium: false
          },
          {
            title: t('menu.language.title'),
            subtitle: t('menu.language.subtitle'),
            icon: 'mdi-translate',
            color: 'white',
            background: 'linear-gradient(135deg, #6366F1 0%, #4F46E5 100%)',
            disabled: false,
            isPremium: false
          },
          {
            title: t('menu.admin_panel.title'),
            subtitle: t('menu.admin_panel.subtitle'),
            icon: 'mdi-shield-account-outline',
            color: 'white',
            background: 'linear-gradient(135deg, #14B8A6 0%, #0D9488 100%)',
            disabled: false,
            isPremium: false
          },
          {
            title: t('menu.slido.title'),
            subtitle: t('menu.slido.subtitle'),
            icon: 'mdi-poll',
            color: 'white',
            background: 'linear-gradient(135deg, #0EA5E9 0%, #0284C7 100%)',
            disabled: false,
            isPremium: false
          },
          {
            title: t('menu.commute.title'),
            subtitle: t('menu.commute.subtitle'),
            icon: 'mdi-train',
            color: 'white',
            background: 'linear-gradient(135deg, #475569 0%, #334155 100%)',
            disabled: false,
            isPremium: false
          },
          {
            title: t('menu.vouchers.title'),
            subtitle: t('menu.vouchers.subtitle'),
            icon: 'mdi-ticket-percent-outline',
            color: 'white',
            background: 'linear-gradient(135deg, #EC4899 0%, #DB2777 100%)',
            disabled: !isPremiumUser.value,
            isPremium: true
          },
          {
            title: t('menu.feedback.title'),
            subtitle: t('menu.feedback.subtitle'),
            icon: 'mdi-message-text-outline',
            color: 'white',
            background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
            disabled: false,
            isPremium: false
          },
          {
            title: t('menu.functions.title'),
            subtitle: t('menu.functions.subtitle'),
            icon: 'mdi-rocket-launch-outline',
            color: 'white',
            background: 'linear-gradient(135deg, #1E293B 0%, #0F172A 100%)',
            disabled: false,
            isPremium: false
          },
          {
            title: t('menu.signups.title'),
            subtitle: t('menu.signups.subtitle'),
            icon: 'mdi-account-multiple-plus-outline',
            color: 'white',
            background: 'linear-gradient(135deg, #A855F7 0%, #9333EA 100%)',
            disabled: false,
            isPremium: false
          },
          {
            title: t('menu.logout.title'),
            subtitle: t('menu.logout.subtitle'),
            icon: 'mdi-logout',
            color: 'white',
            background: 'linear-gradient(135deg, #94A3B8 0%, #64748B 100%)',
            disabled: false,
            isPremium: false
          },
        ]
      }

      watch([isAdmin, isPremiumUser], () => {
        updateMenuItems()
      })

      const goToMenuItem = async item => {
        if (item.disabled) {
          Swal.fire({
            title: t('menu.premium-title'),
            text: t('menu.premium-message'),
            icon: 'info',
            confirmButtonColor: '#285096',
            confirmButtonText: `<span style="color: white; font-weight: 600;">${t('menu.premium-ok')}</span>`,
            customClass: { confirmButton: 'swal-info-btn' },
            buttonsStyling: true
          })
          return
        }

        switch (item.title) {
          case t('menu.profile.title'): router.push({ name: 'UserProfile' }).catch(err => console.error(err)); break
          case t('menu.room_control.title'): router.push({ name: 'RoomControlPois' }).catch(err => console.error(err)); break
          case t('menu.elevator_control.title'): router.push({ name: 'Elevator' }).catch(err => console.error(err)); break
          case t('menu.analytics.title'): router.push({ name: 'WorkSpaceDashboard' }).catch(err => console.error(err)); break
          case t('menu.occupancy.title'): router.push({ name: 'OccupancyView' }).catch(err => console.error(err)); break
          case t('menu.language.title'): showLanguageDialog.value = true; break
          case t('menu.admin_panel.title'): dialogType.value = 'adminPanel'; dialog.value = true; break
          case t('menu.feedback.title'): router.push({ name: 'FeedbackForm' }).catch(err => console.error(err)); break
          case t('menu.incidents.title'): router.push({ name: 'Incidents' }).catch(err => console.error(err)); break
          case t('menu.commute.title'): router.push({ name: 'CommuteDashboard' }).catch(err => console.error(err)); break
          case t('menu.vouchers.title'): router.push({ name: 'Vouchers' }).catch(err => console.error(err)); break
          case t('menu.slido.title'): router.push({ name: 'SlidoView' }).catch(err => console.error(err)); break
          case t('menu.functions.title'): router.push({ name: 'Functions' }).catch(err => console.error(err)); break
          case t('menu.signups.title'): router.push({ name: 'SignupsManagement' }).catch(err => console.error(err)); break
          case t('menu.logout.title'): await handleLogout(); break
          default: console.log('Unknown item clicked'); break
        }
      }

      const goToAdminControl = control => {
        if (control.disabled) {
          dialog.value = false
          Swal.fire({
            title: t('menu.premium-title'),
            text: t('menu.premium-message'),
            icon: 'info',
            confirmButtonColor: '#285096',
            confirmButtonText: `<span style="color: white; font-weight: 600;">${t('menu.premium-ok')}</span>`,
            customClass: { confirmButton: 'swal-info-btn' },
            buttonsStyling: true
          })
          return
        }

        dialog.value = false
        switch (control.title) {
          case 'Feedbacks': router.push({ name: 'AdminFeedbacks' }); break
          case 'Bookings': router.push({ name: 'BookingsManagement' }); break
          case 'Users': router.push({ name: 'UsersManagement' }); break
          case 'Incidents': router.push({ name: 'AdminIncidentsManagement' }); break
          case 'Remote Work': router.push({ name: 'RemoteWorkAdmin' }); break
        }
      }

      async function handleLogout() {
        await performLogout()
      }

      const setLanguage = lang => selectedLanguage.value = lang

      const confirmLanguageChange = async () => {
        showLanguageDialog.value = false
        Swal.fire({
          title: t('language_change.title'),
          text: t('language_change.text'),
          icon: 'warning',
          showCancelButton: true,
          confirmButtonColor: '#28A745',
          cancelButtonColor: '#E74C3C',
          confirmButtonText: `<span style="color: white; font-weight: 600;">${t('language_change.confirm')}</span>`,
          cancelButtonText: `<span style="color: white; font-weight: 600;">${t('language_change.cancel')}</span>`,
          customClass: { confirmButton: 'swal-confirm-btn', cancelButton: 'swal-cancel-btn' },
          buttonsStyling: true
        }).then(async result => {
          if (result.isConfirmed) {
            locale.value = selectedLanguage.value
            await setCookie('language', selectedLanguage.value, 365)
            updateMenuItems()
            window.location.reload()
          } else {
            showLanguageDialog.value = true
          }
        })
      }

      const cancelLanguageChange = () => {
        showLanguageDialog.value = false
      }

      const loadLanguageFromCookie = async () => {
        const savedLanguage = await getCookie('language')
        if (savedLanguage) {
          selectedLanguage.value = savedLanguage
          locale.value = savedLanguage
        } else {
          selectedLanguage.value = 'en'
          locale.value = 'en'
          await setCookie('language', 'en', 365)
        }
        updateMenuItems()
      }

      const goToHardwareControl = control => {
        dialog.value = false
        switch (control.title) {
          case 'Room Control': router.push({ name: 'RoomControlPois' }).catch(err => console.error(err)); break
          default: break
        }
      }

      async function loadUserProfileDetails() {
        try {
          const apiProfiles = await getUserProfiles()
          let apiProfile = lodash.find(apiProfiles, { email: userProfile.value.email })
          if (apiProfile) {
            apiProfile = await getUserProfileById(apiProfile.id)
            userProfile.value.jobTitle = apiProfile.job || 'Employee'
            if (!apiProfile.profileImage) {
              const imageId = lodash.get(apiProfile, 'files.entities', []).find(file => file.type === 'profileImage')?.id
              if (imageId) {
                const profileImageFile = await getFile(imageId)
                const profileImage = profileImageFile ? await getDataFromBlob(profileImageFile) : user_avatar_3d
                userProfile.value.profileImage = profileImage
                apiProfile.profileImage = profileImage
                await storeBlob(`userProfile_${apiProfile.id}`, apiProfile)
              }
            } else {
              userProfile.value.profileImage = apiProfile.profileImage
            }
          }
        } catch (error) {
          console.error('Failed to load user profile details', error)
        } finally {
          if (!userProfile.value.profileImage) {
            userProfile.value.profileImage = user_avatar_3d
          }
          profileLoaded.value = true
        }
      }

      const appendFeatures = async () => {
        try {
          const features = await getFeatures()
          const isInternalUser = userProfile.value?.email?.toLowerCase().includes('@pinestack.io')

          menuItems.value = menuItems.value.filter(item => {
            const title = item.title.toLowerCase()
            switch (title) {
              case t('menu.profile.title').toLowerCase():
                return features?.userOptions?.enabled && features?.userOptions?.profileUpdate
              case t('menu.room_control.title').toLowerCase():
                return false
              case t('menu.elevator_control.title').toLowerCase():
                return false
              case t('menu.analytics.title').toLowerCase():
                return features?.analytics?.enabled
              case t('menu.occupancy.title').toLowerCase():
                return false
              case t('menu.incidents.title').toLowerCase():
                return features?.space?.enabled && features?.space?.inicidentCreation
              case t('menu.language.title').toLowerCase():
                return features?.appSettings?.enabled && features?.appSettings?.languageChange
              case t('menu.admin_panel.title').toLowerCase():
                return features?.admin?.enabled
              case t('menu.slido.title').toLowerCase():
                return features?.userOptions?.enabled && features?.userOptions?.slido
              case t('menu.commute.title').toLowerCase():
                return features?.commute?.enabled === true
              case t('menu.vouchers.title').toLowerCase():
                return true
              case t('menu.feedback.title').toLowerCase():
                return features?.userOptions?.enabled && features?.userOptions?.feedback
              case t('menu.functions.title').toLowerCase():
                return features?.functionsList?.enabled
              case t('menu.signups.title').toLowerCase():
                return isInternalUser
              case t('menu.logout.title').toLowerCase():
                return true
              default:
                return false
            }
          })
        } catch (error) {
          console.error('Failed to load features', error)
        }
      }

      async function checkPremiumAccess() {
        try {
          const userEmail = userProfile.value?.email?.toLowerCase() || ''
          const userRoles = userProfile.value?.realm_access?.roles || []

          const hasAdminRole = userRoles.some(role =>
            role?.toLowerCase()?.includes('admin') ||
            role?.toLowerCase()?.includes('globaladmin')
          )
          const hasPinestackEmail = userEmail.includes('@pinestack.io')

          isPremiumUser.value = hasPinestackEmail || hasAdminRole
        } catch (error) {
          console.error('Failed to check premium access:', error)
          isPremiumUser.value = false
        }
      }

      onMounted(async () => {
        try {
          const [logoImage, userProfileData, webPlatform] = await Promise.all([
            getToolbarLogoImage(),
            OAuthService.getUserProfile(),
            isWebPlatform()
          ])

          userProfile.value = userProfileData

          isPia.value = await isPIA()
          isAdmin.value = userProfileData?.realm_access?.roles?.includes('globaladmin') || false

          await checkPremiumAccess()
          await loadLanguageFromCookie()
          await appendFeatures()

          logo.value = logoImage
          profileLoaded.value = false
          await loadUserProfileDetails()
        } catch (error) {
          console.error(error)
        }
      })

      return {
        menuItems,
        logo,
        userProfile,
        router,
        isAdmin,
        isPremiumUser,
        goToMenuItem,
        user_avatar_3d,
        dialog,
        roomControls,
        goToHardwareControl,
        profileLoaded,
        dialogType,
        goToAdminControl,
        adminControls,
        showLanguageDialog,
        selectedLanguage,
        setLanguage,
        confirmLanguageChange,
        cancelLanguageChange,
        german_flag,
        usa_flag,
        isPia,
        userRoles,
        formatRoleName,
        getRoleColor,
        isMobile
      }
    },
  })
</script>

<style scoped>
/* ===== BASE CONTAINER ===== */
.menu-container {
  min-height: var(--app-viewport-height, 100vh);
  background: linear-gradient(160deg, #E8F4FD 0%, #F1F5F9 40%, #FFFFFF 100%);
  padding: 16px;
  padding-bottom: 100px;
}

:global(.is-native) .menu-container {
  padding-top: 0;
}

/* ===== HEADER ===== */
.header-card {
  background: linear-gradient(135deg, #1E40AF 0%, #3B82F6 50%, #60A5FA 100%);
  padding: 16px 20px;
  position: relative;
  overflow: hidden;
}

.header-card::before {
  content: '';
  position: absolute;
  inset: 0;
  background: url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='0.06'%3E%3Ccircle cx='20' cy='20' r='2'/%3E%3C/g%3E%3C/svg%3E");
}

.header-content {
  display: flex;
  align-items: center;
  gap: 14px;
  position: relative;
  z-index: 1;
}

.logo-wrapper {
  background: white;
  padding: 10px;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.toolbar-logo {
  height: 28px;
  width: auto;
  display: block;
}

.header-title {
  flex: 1;
  font-size: 1.375rem;
  font-weight: 700;
  color: white;
  letter-spacing: -0.5px;
}

.refresh-btn {
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(10px);
}

/* ===== PROFILE CARD ===== */
.profile-card {
  background: white;
  padding: 20px;
  border: 1px solid rgba(0, 0, 0, 0.04);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04);
}

.profile-content {
  display: flex;
  align-items: center;
  gap: 16px;
}

.avatar-section {
  position: relative;
  flex-shrink: 0;
}

.avatar-glow {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 100px;
  height: 100px;
  border-radius: 50%;
  background: linear-gradient(135deg, #3B82F6, #60A5FA);
  opacity: 0.15;
  filter: blur(16px);
}

.profile-avatar {
  border: 4px solid white;
  box-shadow: 0 8px 24px rgba(59, 130, 246, 0.2);
  position: relative;
}

.online-indicator {
  position: absolute;
  bottom: 4px;
  right: 4px;
  width: 22px;
  height: 22px;
  background: linear-gradient(135deg, #22C55E, #16A34A);
  border: 3px solid white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(34, 197, 94, 0.4);
}

.profile-details {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.user-name {
  font-size: 1.25rem;
  font-weight: 700;
  color: #0F172A;
  margin: 0;
  line-height: 1.2;
  letter-spacing: -0.5px;
}

.job-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #EFF6FF;
  padding: 5px 12px;
  border-radius: 20px;
  width: fit-content;
}

.job-badge span {
  font-size: 0.8125rem;
  font-weight: 600;
  color: #1E40AF;
}

.email-row {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #64748B;
  font-size: 0.8125rem;
}

.roles-row {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 2px;
}

.role-chip {
  font-size: 0.625rem;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  height: 22px;
}

.edit-profile-btn {
  margin-left: auto;
  box-shadow: 0 4px 12px rgba(30, 64, 175, 0.25);
}

/* ===== MENU GRID ===== */
.menu-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}

.menu-item {
  cursor: pointer;
}

/* ===== MENU CARDS - ELEGANT DESIGN ===== */
.menu-card {
  background: white;
  border-radius: 20px;
  padding: 18px 14px;
  position: relative;
  overflow: hidden;
  border: 1px solid rgba(0, 0, 0, 0.03);
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  height: 100%;
  min-height: 140px;
  display: flex;
  flex-direction: column;
}

.menu-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: transparent;
  transition: background 0.3s ease;
}

.menu-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.1);
  border-color: transparent;
}

.menu-card:hover::before {
  background: linear-gradient(90deg, #3B82F6, #60A5FA);
}

.menu-card:active {
  transform: translateY(-2px);
}

.card-disabled {
  opacity: 0.7;
  pointer-events: none;
}

/* Premium Tag */
.premium-tag {
  position: absolute;
  top: 10px;
  right: 10px;
  background: linear-gradient(135deg, #F59E0B, #D97706);
  color: white;
  font-size: 0.5625rem;
  font-weight: 700;
  letter-spacing: 0.5px;
  padding: 4px 8px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  gap: 3px;
  box-shadow: 0 2px 8px rgba(245, 158, 11, 0.4);
  z-index: 10;
}

/* Locked Overlay */
.locked-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, rgba(15, 23, 42, 0.6), rgba(30, 41, 59, 0.5));
  backdrop-filter: blur(2px);
  border-radius: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  z-index: 5;
}

.locked-text {
  color: white;
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
}

/* Card Inner Content */
.card-inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  flex: 1;
  gap: 12px;
}

.icon-box {
  width: 52px;
  height: 52px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.menu-card:hover .icon-box {
  transform: scale(1.08) rotate(-3deg);
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.2);
}

.card-icon {
  font-size: 26px;
}

.card-text {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.card-title {
  font-size: 0.9375rem;
  font-weight: 700;
  color: #0F172A;
  line-height: 1.2;
  letter-spacing: -0.3px;
}

.card-desc {
  font-size: 0.75rem;
  color: #64748B;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* Hover Arrow */
.card-arrow {
  position: absolute;
  bottom: 12px;
  right: 12px;
  width: 28px;
  height: 28px;
  background: linear-gradient(135deg, #3B82F6, #2563EB);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transform: translateX(-8px);
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
}

.menu-card:hover .card-arrow {
  opacity: 1;
  transform: translateX(0);
}

/* ===== DIALOGS ===== */
.dialog-card {
  overflow: hidden;
}

.dialog-header {
  background: linear-gradient(135deg, #1E40AF 0%, #3B82F6 100%);
  color: white;
  font-weight: 600;
  padding: 16px 20px;
  display: flex;
  align-items: center;
}

.dialog-body {
  padding: 20px;
  background: #F8FAFC;
}

.dialog-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}

.dialog-item {
  background: white;
  border-radius: 16px;
  padding: 20px 12px;
  text-align: center;
  cursor: pointer;
  position: relative;
  border: 1px solid #E2E8F0;
  transition: all 0.3s ease;
}

.dialog-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.08);
  border-color: #3B82F6;
}

.dialog-item.item-disabled {
  opacity: 0.6;
  pointer-events: none;
}

.dialog-premium-tag {
  position: absolute;
  top: 8px;
  right: 8px;
  background: linear-gradient(135deg, #F59E0B, #D97706);
  color: white;
  font-size: 0.5rem;
  font-weight: 700;
  padding: 3px 6px;
  border-radius: 4px;
}

.dialog-locked {
  position: absolute;
  inset: 0;
  background: rgba(15, 23, 42, 0.5);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.dialog-icon {
  width: 56px;
  height: 56px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 10px;
}

.dialog-title {
  font-size: 0.8125rem;
  font-weight: 600;
  color: #0F172A;
}

/* ===== LANGUAGE DIALOG ===== */
.language-dialog {
  overflow: hidden;
}

.lang-header {
  background: #F1F5F9;
  font-weight: 600;
  color: #0F172A;
  padding: 16px 20px;
}

.lang-body {
  padding: 20px;
}

.lang-options {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.lang-option {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  border-radius: 14px;
  border: 2px solid #E2E8F0;
  cursor: pointer;
  transition: all 0.3s ease;
}

.lang-option:hover {
  border-color: #3B82F6;
  background: #F8FAFC;
}

.lang-option.lang-selected {
  border-color: #22C55E;
  background: #F0FDF4;
}

.lang-flag {
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.lang-name {
  flex: 1;
  font-size: 1rem;
  font-weight: 600;
  color: #0F172A;
}

.lang-actions {
  padding: 16px 20px;
  background: #F8FAFC;
  border-top: 1px solid #E2E8F0;
}

/* ===== RESPONSIVE ===== */

/* Small mobile (< 380px) */
@media (max-width: 379px) {
  .menu-container {
    padding: 12px;
  }

  :global(.is-native) .menu-container {
    padding-top: 0;
  }
  
  .header-card {
    padding: 14px 16px;
  }
  
  .header-title {
    font-size: 1.1875rem;
  }
  
  .toolbar-logo {
    height: 24px;
  }
  
  .profile-card {
    padding: 16px;
  }
  
  .profile-content {
    gap: 12px;
  }
  
  .avatar-glow {
    width: 80px;
    height: 80px;
  }
  
  .user-name {
    font-size: 1.0625rem;
  }
  
  .job-badge {
    padding: 4px 10px;
  }
  
  .job-badge span {
    font-size: 0.75rem;
  }
  
  .email-row {
    font-size: 0.75rem;
  }
  
  .menu-grid {
    gap: 10px;
  }
  
  .menu-card {
    padding: 14px 12px;
    min-height: 130px;
    border-radius: 16px;
  }
  
  .icon-box {
    width: 46px;
    height: 46px;
    border-radius: 14px;
  }
  
  .card-icon {
    font-size: 22px;
  }
  
  .card-title {
    font-size: 0.8125rem;
  }
  
  .card-desc {
    font-size: 0.6875rem;
    -webkit-line-clamp: 1;
  }
  
  .premium-tag {
    top: 8px;
    right: 8px;
    padding: 3px 6px;
    font-size: 0.5rem;
  }
  
  .card-arrow {
    width: 24px;
    height: 24px;
    bottom: 10px;
    right: 10px;
  }
}

/* Medium mobile (380px - 599px) */
@media (min-width: 380px) and (max-width: 599px) {
  .menu-container {
    padding: 14px;
  }

  :global(.is-native) .menu-container {
    padding-top: 0;
  }
  
  .menu-card {
    min-height: 145px;
  }
  
  .icon-box {
    width: 50px;
    height: 50px;
  }
  
  .card-icon {
    font-size: 24px;
  }
}

/* Tablet (600px+) */
@media (min-width: 600px) {
  .menu-container {
    padding: 20px;
    max-width: 900px;
    margin: 0 auto;
  }

  :global(.is-native) .menu-container {
    padding-top: 0;
  }
  
  .header-card {
    padding: 20px 28px;
  }
  
  .header-title {
    font-size: 1.5rem;
  }
  
  .toolbar-logo {
    height: 34px;
  }
  
  .profile-card {
    padding: 28px;
  }
  
  .profile-content {
    gap: 24px;
  }
  
  .avatar-glow {
    width: 120px;
    height: 120px;
  }
  
  .user-name {
    font-size: 1.5rem;
  }
  
  .menu-grid {
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
  }
  
  .menu-card {
    padding: 24px 18px;
    min-height: 170px;
  }
  
  .icon-box {
    width: 60px;
    height: 60px;
    border-radius: 18px;
  }
  
  .card-icon {
    font-size: 30px;
  }
  
  .card-inner {
    gap: 14px;
  }
  
  .card-title {
    font-size: 1rem;
  }
  
  .card-desc {
    font-size: 0.8125rem;
  }
}

/* Desktop (960px+) */
@media (min-width: 960px) {
  .menu-container {
    max-width: 100%;
  }
  
  .menu-grid {
    grid-template-columns: repeat(4, 1fr);
    gap: 20px;
  }
  
  .menu-card {
    min-height: 180px;
    padding: 28px 20px;
  }
}

/* Large desktop (1280px+) */
@media (min-width: 1280px) {
  .menu-container {
    max-width: 100%;
  }
  
  .menu-grid {
    grid-template-columns: repeat(5, 1fr);
  }
}

/* ===== ANIMATIONS ===== */
.menu-item {
  animation: fadeInUp 0.4s ease-out backwards;
}

.menu-item:nth-child(1) { animation-delay: 0.05s; }
.menu-item:nth-child(2) { animation-delay: 0.08s; }
.menu-item:nth-child(3) { animation-delay: 0.11s; }
.menu-item:nth-child(4) { animation-delay: 0.14s; }
.menu-item:nth-child(5) { animation-delay: 0.17s; }
.menu-item:nth-child(6) { animation-delay: 0.20s; }
.menu-item:nth-child(7) { animation-delay: 0.23s; }
.menu-item:nth-child(8) { animation-delay: 0.26s; }
.menu-item:nth-child(9) { animation-delay: 0.29s; }
.menu-item:nth-child(10) { animation-delay: 0.32s; }
.menu-item:nth-child(n+11) { animation-delay: 0.35s; }

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(16px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* ===== SWEETALERT ===== */
:deep(.swal-confirm-btn),
:deep(.swal-cancel-btn),
:deep(.swal-info-btn) {
  padding: 12px 28px !important;
  border-radius: 10px !important;
  font-weight: 600 !important;
}
</style>
